import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Helmet } from 'react-helmet';
import { Link, Redirect } from 'react-router-dom';

// Social helpers for facebook and twitter auth
import * as Social  from '../../utils/social';

// Actions
import {
  logIn,
  updateUserState,
} from '../../actions/user';

import {
  setActiveProfile,
} from '../../actions/activeProfile';

import {
  getProfileAccountDetails
} from '../../actions/accountDetails'

import {
  updateForm,
} from '../../actions/onboarding';

// Components
import Header from '../../components/Global/Header';
import FormContainer from '../../components/Global/FormContainer';
import FormInput from '../../components/Global/Forms/FormInput';
import FlashMessage from '../../components/Global/FlashMessage';

// Utils
import {
  validateEmail,
  setCookie,
} from '../../utils/helpers'

class LogIn extends Component {

  state = {
    input: {
      email: '',
      password: '',
    },
    formErrors: {
      email: '',
      password: '',
    },
    formIsValid: false,
    displayErrorMessages: false,
    logInErrorMessage: '',
  }

  handleInputChange = (event) => {
    const { value, name } = event.target;

    this.setState(state => ({
      ...state,
      input: {
        ...state.input,
        [name]: value
      },
      logInErrorMessage: ''
    }), () => { this.updateFieldErrorMessage(name, value) });
  }

  handleIncompleteUserRedirect = (currentForm, prevForm) => {
    this.props.updateOnboardingForm({
      prevForm,
      currentForm
    })
    this.props.history.push(`/sign-up/${currentForm}`);
  }

  handleSubmit = (event) => {
    event.preventDefault();

    if (this.isFormValid()) {
      const userForSubmit = this.state.input;

      this.props.logInUser(userForSubmit)
        .then((response) => {
          // Set success cookie
          setCookie('ll_user_logged_in', true, 100);

          //////////////////////////////////////////////////////////////
          // User successfully signs in on BE
          //////////////////////////////////////////////////////////////
          if (response.success) {
            let userRole = response.user.role

            if (userRole === "super admin" || userRole === "admin") {
              const { user } = this.props;
              const combinedUser = Object.assign(user, response.user);

              combinedUser.isLoggedIn = true
              combinedUser.reduxLogInComplete = true

              // Update the redux store to the current user
              this.props.updateReduxUser(combinedUser)
              this.props.history.push('/admin/profiles');
            } else {
              const { user } = this.props;
              const combinedUser = Object.assign(user, response.user);

              // Update the redux store to the current user
              this.props.updateReduxUser(combinedUser)

              // Check to see if user is a maintainer of any profiles.
              // If they aren't, that means that they've requested access
              // to a profile and are waiting to be accepted.
              const maintainedProfiles = response.user.maintained_profiles;
              if (maintainedProfiles.length > 0) {
                // Get the first username maintained for storage in Redux store.
                const username = maintainedProfiles[0].username
                this.initalizeReduxAndContinue(user, username)
              } else if (user.role === 'athlete') {
                this.handleIncompleteUserRedirect('athlete-info-continue', 'log-in')
              } else {
                // User is a parent/guardian without any maintaine profiles.
                // Push them back into onboarding flow
                this.handleIncompleteUserRedirect('connect-account-continue', 'log-in')
              }
            }
          } else {
            //////////////////////////////////////////////////////////////
            // User does not sign in successfully on BE.
            // Show them an error message:
            //////////////////////////////////////////////////////////////
            if (response.message === 'Invalid email or password') {
              this.setLogInError('Invalid email or password. If you signed up with Facebook or Twitter, please try logging in with one of those above.');
            } else {
              this.setLogInError(response.message)
            }
          }
        })
    } else {
      this.setFormErrorState();
    }
  }

  initalizeReduxAndContinue(user, currentAthleteUsername) {
    //////////////////////////////////////////////////////////////
    // Set the `currentAthlete` redux object to the first
    // maintained profile of the user logging in.
    //////////////////////////////////////////////////////////////
    this.props.setActiveProfile(currentAthleteUsername)
    .then(response => {
      if (response.success) {
        this.props.setReduxAccountDetails(user, currentAthleteUsername)
        .then(response => { this.completeLogin() })
      }
    })
  }

  completeLogin() {
    //////////////////////////////////////////////////////////////
    // Complete logging in by setting a `reduxLogInComplete` flag on
    // our Redux user.
    //
    // This lets other components know that all Redux
    // and API functions have completed successfully and we have
    // the expected data to run off of.
    //////////////////////////////////////////////////////////////
    let userLoggedIn  = {...this.props.user}

    userLoggedIn.reduxLogInComplete = true
    this.props.updateReduxUser(userLoggedIn)
  }

  setFormErrorState = () => {
    const { input, displayErrorMessages } = this.state;

    Object.keys(input).map((field, i) => {
      return this.updateFieldErrorMessage(field, input[field])
    })

    if (!this.state.formIsValid && !displayErrorMessages) {
      this.toggleErrorMessages();
    }
  }

  turnOnErrorMessages() {
    this.setState(state => ({
      ...state,
      displayErrorMessages: true
    }));
  }

  toggleErrorMessages() {
    const { displayErrorMessages } = this.state;

    this.setState(state => ({
      ...state,
      displayErrorMessages: !displayErrorMessages
    }));
  }

  isFormValid() {
    const { input } = this.state;

    let inputErrorIndex = Object.keys(input).map((field, i) => {
      return this.isFieldValid(field, input[field])
    }).indexOf(false)

    if (inputErrorIndex > -1) {
      return false;
    } else {
      return true;
    }
  }

  isFieldValid = (fieldName, value) => {
    switch(fieldName) {
      case 'email':
        return validateEmail(value)
        // eslint-disable-next-line no-unreachable
        break;
      default:
        return value.length > 0
        // eslint-disable-next-line no-unreachable
        break;
    }
  }

  updateFieldErrorMessage = (fieldName, value) => {
    let errorMessage = '';

    switch(fieldName) {
      case 'email':
        errorMessage = validateEmail(value)
          ? ''
          : 'Please enter a valid email'
        break;
      default:
        errorMessage = value.length > 0
          ? ''
          : 'Please complete this field';
        break;
    }

    this.setState(state => ({
      ...state,
      formErrors: {
        ...state.formErrors,
        [fieldName]: errorMessage
      }
    }));
  }

  handleInputBlur = (event) => {
    const { value, name } = event.target;
    const { formIsValid, displayErrorMessages } = this.state;

    this.updateFieldErrorMessage(name, value);

    if (!formIsValid && !displayErrorMessages) {
      this.toggleErrorMessages();
    }
  }

  setLogInError(message) {
    this.setState({
      logInErrorMessage: message ? message : ''
    });
  }

  renderLogInErrorMessage() {
    const { logInErrorMessage } = this.state;

    return logInErrorMessage.length > 0
    ? (
      <FlashMessage optionalClasses='error'>
        {logInErrorMessage}
      </FlashMessage>
    )
    : ''
  }

  redirectLoggedInUser() {
    const { user, activeProfile } = this.props;

    // If the user is an admin, send them to...
    if (user.role === 'admin' || user.role === 'super admin') {
      return <Redirect to={{ pathname: '/admin/profiles' }}/>
    }
    // If user maintains profiles, send them to their dashboard
    else if (user.maintained_profiles.length > 0) {
      if (activeProfile.participations.length === 0) {
        const user = {
          ...this.props.user,
          isOnboarding: true,
        }
        this.props.updateReduxUser(user)
        return <Redirect to={{ pathname: '/sign-up/sport-selection' }}/>
      } else {
        return <Redirect to={{ pathname: '/' }}/>
      }
    }
    // If has no maintained profiles and is role === 'parent'
    // go to `/sign-up/connect-account-continue`
    else if (user.role === 'parent') {
      return <Redirect to={{ pathname: '/sign-up/connect-account-continue' }}/>
    }
    // If user doesn't maintain profile and is an athlete,
    // go to `/sign-up/athlete-info`
    else {
      return <Redirect to={{ pathname: '/sign-up/athlete-info' }}/>
    }
  }

  handleFacebookAuth = () => {
    Social.startFacebookAuth();
  }

  handleTwitterAuth = () => {
    Social.startTwitterAuth()
    .then((response) => {
      window.location.href = response.auth_url;
    }, (error) => {
      this.setLogInError(error);
    });
  }

  render() {
    const {
      input,
      formErrors,
      displayErrorMessages,
    } = this.state;

    const {
      appName,
      path,
    } = this.props;

    const { isLoggedIn, reduxLogInComplete, isOnboarding } = this.props.user;

    if (isLoggedIn && (reduxLogInComplete || isOnboarding)) {
      return this.redirectLoggedInUser()
    } else {
      return (
        <div className="onboarding log-in">
          <Helmet>
            <title>{appName} | Log In</title>
          </Helmet>
          <Header path={ path }/>
          <FormContainer>
            <header>
              <h2>Log In to Locker Linked</h2>
              <p>
                Don't have an account?&nbsp;
                <Link
                  to={'/sign-up'}
                  className="text-link"
                >
                  Sign Up
                </Link>
              </p>
              {this.renderLogInErrorMessage()}
              <div className="social-sign-up-container">
                <a className="social-sign-up facebook" onClick={this.handleFacebookAuth}>
                  <svg><use xlinkHref="#facebook"/></svg>
                  <span>Log In with Facebook</span>
                </a>
                <a className="social-sign-up twitter" onClick={this.handleTwitterAuth}>
                  <svg><use xlinkHref="#twitter"/></svg>
                  <span>Log In with Twitter</span>
                </a>
              </div>
            </header>
            <form onSubmit={this.handleSubmit}>
              <FormInput
                autoComplete="off"
                label="Email Address"
                placeholder="Email Address"
                name="email"
                type="email"
                value={input.email}
                onChange={this.handleInputChange}
                onBlur={this.handleInputBlur}
                showError={displayErrorMessages}
                errorMessage={formErrors.email}
              />
              <FormInput
                label="Password"
                placeholder="Password"
                name="password"
                type="password"
                value={input.password}
                onChange={this.handleInputChange}
                showError={displayErrorMessages}
                errorMessage={formErrors.password}
              />
              <hr />
              <input type="submit" value="Continue" />
            </form>
            <div className="post-form-link">
              <Link to="/reset-password" className="text-link">
                Forgot Password?
              </Link>
            </div>
          </FormContainer>
        </div>
      )
    }
  }
}

const mapStateToProps =  state  => {
  return {
    user: state.user,
    activeProfile: state.activeProfile,
  }
}

const mapDispatchToProps = (dispatch, ownProps) => {
  return {
    logInUser: (user) => dispatch(logIn(user)),
    updateReduxUser: (user) => dispatch(updateUserState(user)),
    setActiveProfile: (username) => dispatch(setActiveProfile(username)),
    setReduxAccountDetails: (user, username) => dispatch(getProfileAccountDetails(user, username)),
    updateOnboardingForm: formData => {
      dispatch(updateForm(formData))
    },
  }
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(LogIn)



// WEBPACK FOOTER //
// ./src/screens/Sessions/LogIn.js